import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import ClosedCaptionDisabledIcon from '@mui/icons-material/ClosedCaptionDisabled';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Box, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEditor } from './EditorContext';
import TransportControls from './TransportControls';
import { getOutputDuration, outputToOriginalTime } from './editorUtils';

/* ── Styles ─────────────────────────────────────────────────────── */

const MonitorRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #0a0a12;
    overflow: hidden;
`;

const VideoArea = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
    position: relative;
    min-height: 0;
    overflow: hidden;
`;

const VideoEl = styled.video`
    max-width: 100%;
    max-height: 100%;
    background: #000;
    display: block;
`;

const SubOverlay = styled.div`
    position: absolute;
    top: 78%;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    display: flex;
    justify-content: center;
    text-align: center;
    pointer-events: none;
    z-index: 10;
`;

const SubText = styled.div`
    display: block;
    width: 100%;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: clamp(6px, 0.9vw, 12px);
    padding: 4px 12px;
    border-radius: 3px;
    line-height: 1.3;
    white-space: pre-line;
    max-width: 100%;
    word-break: break-word;
`;

const MonitorHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    background: #161625;
    border-bottom: 1px solid #2a2a3a;
    flex-shrink: 0;
`;

const CutBadge = styled.div`
    background: rgba(229, 57, 53, 0.2);
    border: 1px solid #e53935;
    color: #ef9a9a;
    font-size: 10px;
    padding: 1px 8px;
    border-radius: 10px;
    white-space: nowrap;
`;

/* ── Component ───────────────────────────────────────────────────── */

/**
 * ProgramMonitor — preview panel that plays the video, skipping cut regions.
 *
 * It binds to a <video> element and hooks into timeupdate to skip over
 * any deleted segments in real-time.
 */
export default function ProgramMonitor() {
    const { state, setDuration, setPlayhead, play, pause, toggleSubs, toggleMuteVideo, setVideoVolume, setOverlayAudio } = useEditor();
    const {
        videoUrl,
        videoTitle,
        duration,
        cuts,
        cutMarkers,
        isPlaying,
        playheadTime,
        previewLang,
        previewSubsOn,
        subtitlesByLanguage,
        muteVideoAudio,
        audioTracks,
        logoTracks,
        videoStartOffset,
        videoEndOffset,
        videoVolume,
        playOverlayAudio,
    } = state;

    const outputDuration = getOutputDuration(duration, cuts);

    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const isSeeking = useRef(false);
    // Map of trackId → HTMLAudioElement for previewing audio tracks
    const audioElemsRef = useRef({});
    // Map of logo video id -> HTMLVideoElement for overlayed outro/intro videos
    const logoVideoElsRef = useRef({});
    // Ref to VideoArea for overlay measurement
    const videoAreaRef = useRef(null);
    // Measured bounding box of the video element relative to VideoArea

    /* ── Refs for stale-closure prevention in RAF callbacks ────── */
    const playheadRef = useRef(playheadTime);
    useEffect(() => { playheadRef.current = playheadTime; }, [playheadTime]);

    const isPlayingRef = useRef(isPlaying);
    useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

    const videoStartOffsetRef = useRef(videoStartOffset || 0);
    useEffect(() => { videoStartOffsetRef.current = videoStartOffset || 0; }, [videoStartOffset]);

    const videoEndOffsetRef = useRef(videoEndOffset || 0);
    useEffect(() => { videoEndOffsetRef.current = videoEndOffset || 0; }, [videoEndOffset]);

    const outputDurationRef = useRef(outputDuration);
    useEffect(() => { outputDurationRef.current = outputDuration; }, [outputDuration]);
    

    const playRef = useRef(play);
    useEffect(() => { playRef.current = play; }, [play]);

    const pauseRef = useRef(pause);
    useEffect(() => { pauseRef.current = pause; }, [pause]);

    const setPlayheadRef = useRef(setPlayhead);
    useEffect(() => { setPlayheadRef.current = setPlayhead; }, [setPlayhead]);

    /* ── Outro active state (video ended + post-roll exists) ────── */
    const [outroActive, setOutroActive] = useState(false);
    const [videoRect, setVideoRect] = useState({ left: 0, top: 0, width: 0, height: 0 });

    /* ── Measure video element position inside VideoArea ───────────── */
    useEffect(() => {
        const measure = () => {
            const videoEl = videoRef.current;
            const areaEl = videoAreaRef.current;
            if (!videoEl || !areaEl) return;
            const vBox = videoEl.getBoundingClientRect();
            const aBox = areaEl.getBoundingClientRect();
            setVideoRect({
                left: vBox.left - aBox.left,
                top: vBox.top - aBox.top,
                width: vBox.width,
                height: vBox.height,
            });
        };
        const ro = new ResizeObserver(measure);
        const videoEl = videoRef.current;
        const areaEl = videoAreaRef.current;
        if (videoEl) ro.observe(videoEl);
        if (areaEl) ro.observe(areaEl);
        return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoUrl]); // remeasure when video element remounts

    /* ── Mute sync ──────────────────────────────────────────────── */
    useEffect(() => {
        const el = videoRef.current;
        if (el) el.muted = muteVideoAudio;
    }, [muteVideoAudio]);

    /* ── Volume sync ────────────────────────────────────────────── */
    useEffect(() => {
        const el = videoRef.current;
        if (el && typeof state.videoVolume === 'number') el.volume = state.videoVolume;
    }, [state.videoVolume]);

    /* ── Sync isPlaying reducer state from actual video play/pause events ── */
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const onPlay  = () => play();
        const onPause = () => pause();
        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onPause);
        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('pause', onPause);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoUrl]); // re-attach when video remounts

    /* ── Audio track elements: create / update / destroy ──────────────── */
    useEffect(() => {
        const currentIds = new Set(audioTracks.map((t) => t.id));
        // Remove stale elements
        Object.keys(audioElemsRef.current).forEach((id) => {
            if (!currentIds.has(id)) {
                audioElemsRef.current[id].pause();
                delete audioElemsRef.current[id];
            }
        });
        // Create or update
        audioTracks.forEach((t) => {
            if (!audioElemsRef.current[t.id]) {
                // crossOrigin must be set BEFORE src to satisfy COEP (require-corp).
                // Without it the browser uses no-cors (opaque) mode and COEP blocks it.
                const el = new Audio();
                el.crossOrigin = 'anonymous';
                el.src    = t.url;
                el.volume = t.volume ?? 1;
                el.muted  = t.muted  ?? false;
                audioElemsRef.current[t.id] = el;
            } else {
                audioElemsRef.current[t.id].volume = t.volume ?? 1;
                audioElemsRef.current[t.id].muted  = t.muted  ?? false;
            }

            // Sync play state: the sync-effect cleanup pauses all elements whenever
            // audioTracks changes. For any track that should be playing (video is
            // running and track is not muted), restart it here.
            const audioEl = audioElemsRef.current[t.id];
            const videoEl = videoRef.current;
            if (audioEl && videoEl && !videoEl.paused && !(t.muted ?? false)) {
                const audioTime = videoEl.currentTime - (t.offsetSeconds ?? 0);
                if (audioTime >= 0 && audioEl.paused) {
                    if (audioEl.readyState >= 1) audioEl.currentTime = audioTime;
                    audioEl.play().catch((err) => {
                        console.warn(`[AudioTrack] play() failed for "${t.name}":`, err?.name, err?.message);
                    });
                }
            }
        });
    }, [audioTracks]);

    /* ── Sync audio elements with video play / pause / seek ───────────── */
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) return;

        const syncAudioPosition = (videoTime) => {
            audioTracks.forEach((t) => {
                const audioEl = audioElemsRef.current[t.id];
                // Skip seek if audio hasn't loaded metadata yet — setting currentTime
                // on an unready element can corrupt its state in some browsers.
                if (!audioEl || audioEl.readyState < 1) return;
                const audioTime = videoTime - (t.offsetSeconds ?? 0);
                if (audioTime >= 0 && audioTime <= (audioEl.duration || Infinity)) {
                    audioEl.currentTime = audioTime;
                } else {
                    audioEl.currentTime = 0;
                }
            });
        };

        const onPlay = () => {
            audioTracks.forEach((t) => {
                const audioEl = audioElemsRef.current[t.id];
                if (!audioEl || t.muted) return;
                const audioTime = videoEl.currentTime - (t.offsetSeconds ?? 0);
                if (audioTime >= 0) {
                    if (audioEl.readyState >= 1) audioEl.currentTime = audioTime;
                    audioEl.play().catch((err) => {
                        console.warn(`[AudioTrack] play() failed for "${t.name}":`, err?.name, err?.message);
                    });
                }
            });
        };
        const onPause = () => {
            Object.values(audioElemsRef.current).forEach((a) => a.pause());
        };
        const onSeeked = () => syncAudioPosition(videoEl.currentTime);

        videoEl.addEventListener('play',   onPlay);
        videoEl.addEventListener('pause',  onPause);
        videoEl.addEventListener('seeked', onSeeked);
        return () => {
            videoEl.removeEventListener('play',   onPlay);
            videoEl.removeEventListener('pause',  onPause);
            videoEl.removeEventListener('seeked', onSeeked);
            Object.values(audioElemsRef.current).forEach((a) => a.pause());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioTracks, videoUrl]);

    // Cleanup all audio on unmount
    useEffect(() => {
        return () => {
            Object.values(audioElemsRef.current).forEach((a) => a.pause());
            audioElemsRef.current = {};
        };
    }, []);

    /* ── Video source setup (HLS only — plain MP4/blob URLs use src prop directly) */

    useEffect(() => {
        const el = videoRef.current;
        if (!el || !videoUrl) return;

        const isHls = videoUrl.includes('.m3u8');
        if (!isHls) return; // src is set via JSX prop; no extra work needed

        if (Hls.isSupported()) {
            const hls = new Hls({ startLevel: -1 });
            hlsRef.current = hls;
            hls.loadSource(videoUrl);
            hls.attachMedia(el);
        } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari native HLS
            el.src = videoUrl;
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [videoUrl]);

    /* ── Duration ───────────────────────────────────────────────── */

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const onMeta = () => setDuration(el.duration || 0);
        el.addEventListener('loadedmetadata', onMeta);
        if (el.readyState >= 1) onMeta();
        return () => el.removeEventListener('loadedmetadata', onMeta);
    }, [setDuration]);

    /* ── Playback ended ─────────────────────────────────────────── */

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const onEnd = () => {
            if (videoEndOffsetRef.current > 0) {
                // Transition to outro: freeze video at last frame, start ticker
                setPlayheadRef.current(
                    videoStartOffsetRef.current + outputDurationRef.current,
                );
                setOutroActive(true);
            } else {
                pause();
            }
        };
        el.addEventListener('ended', onEnd);
        return () => el.removeEventListener('ended', onEnd);
    }, [pause]);

    /* ── Pre-roll ticker: advance playhead during intro region ───── */
    useEffect(() => {
        if (!isPlaying) {
            setOutroActive(false); // Cancel outro if user pauses
            return;
        }
        const el = videoRef.current;
        const offset = videoStartOffset || 0;
        const endOff = videoEndOffset || 0;
        const ph = playheadRef.current;

        // If playhead is already in outro region when play is pressed
        if (endOff > 0 && ph >= offset + outputDuration) {
            setOutroActive(true);
            return;
        }

        // Not in intro region - video element handles playback
        if (offset === 0 || ph >= offset) return;

        // In intro region: run RAF ticker to advance playhead, then start video
        const startTs = performance.now();
        const startPh = ph;
        let animId;
        const tick = (now) => {
            const next = startPh + (now - startTs) / 1000;
            if (next >= offset) {
                setPlayheadRef.current(offset);
                if (el) {
                    el.currentTime = 0;
                    el.play().catch(() => {});
                }
                return; // Stop ticking — video's timeupdate takes over
            }
            setPlayheadRef.current(next);
            animId = requestAnimationFrame(tick);
        };
        animId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(animId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying, videoStartOffset, videoEndOffset, outputDuration]);

    /* ── Outro ticker: advance playhead during post-roll region ─── */
    useEffect(() => {
        if (!outroActive) return;
        const offset = videoStartOffsetRef.current;
        const outDur = outputDurationRef.current;
        const startPh = Math.max(playheadRef.current, offset + outDur);
        const startTs = performance.now();
        let animId;
        const tick = (now) => {
            const next = startPh + (now - startTs) / 1000;
            const totalEnd =
                videoStartOffsetRef.current +
                outputDurationRef.current +
                videoEndOffsetRef.current;
            if (next >= totalEnd) {
                setPlayheadRef.current(totalEnd);
                setOutroActive(false);
                pauseRef.current();
                return;
            }
            setPlayheadRef.current(next);
            animId = requestAnimationFrame(tick);
        };
        animId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(animId);
    }, [outroActive]);

    /* ── Skip cut regions during playback ──────────────────────── */

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;

        const onTimeUpdate = () => {
            if (isSeeking.current) return;
            const t = el.currentTime;
            // Remember this value so the playheadTime→seek effect can
            // distinguish an internal update (from us) from an external one
            // (user scrubbing the timeline).  Without this the effect would
            // seek the video back to its own slightly-stale position every
            // frame, causing the "stuck repeating" loop.
            lastReportedVideoTime.current = t;
            // Report in output-time (pre-roll offset + video time)
            setPlayhead(t + (videoStartOffset || 0));

            // Check if we're in a deleted region
            for (const cut of cuts) {
                if (t >= cut.startTime && t < cut.endTime) {
                    isSeeking.current = true;
                    el.currentTime = cut.endTime;
                    setTimeout(() => {
                        isSeeking.current = false;
                    }, 50);
                    return;
                }
            }
        };

        el.addEventListener('timeupdate', onTimeUpdate);
        return () => el.removeEventListener('timeupdate', onTimeUpdate);
    }, [cuts, setPlayhead, videoStartOffset]);

    /* ── Seek when playheadTime changes externally (timeline scrub) */

    // Tracks the last video-element time we reported via setPlayhead in
    // onTimeUpdate.  The playheadTime→seek effect checks against this to
    // avoid seeking the video back to its own (slightly stale) position.
    const lastReportedVideoTime = useRef(-1);
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        // Compute video-element time from output playhead
        const videoTime = Math.max(0, playheadTime - (videoStartOffset || 0));
        // If this playheadTime was just set by our own onTimeUpdate, skip —
        // the video is already past this point and seeking back would repeat.
        if (Math.abs(videoTime - lastReportedVideoTime.current) < 0.05) return;
        if (Math.abs(el.currentTime - videoTime) > 0.2) {
            el.currentTime = videoTime;
        }
    }, [playheadTime, videoStartOffset]);

    /* ── Space bar play/pause ───────────────────────────────────── */

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const onKey = (e) => {
            if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
            if (e.code === 'Space') {
                e.preventDefault();
                if (isPlayingRef.current) {
                    // Pause regardless of where we are
                    el.pause();
                    pauseRef.current();
                } else {
                    const offset = videoStartOffsetRef.current;
                    const endOff = videoEndOffsetRef.current;
                    const ph = playheadRef.current;
                    const inOffVideo =
                        (offset > 0 && ph < offset) ||
                        (endOff > 0 && ph >= offset + outputDurationRef.current);
                    if (inOffVideo) {
                        playRef.current(); // Reducer state only; ticker handles playback
                    } else {
                        el.play().catch(() => {});
                    }
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []); // Empty deps — all values accessed via refs

    /* ── Active subtitle cue (RAF-based — reads currentTime at ~60 fps) ── */
    //
    // Using playheadTime (updated by timeupdate every ~250 ms) caused subtitles
    // to appear/disappear up to 250 ms late.  Instead, poll videoRef.current
    // .currentTime directly in a requestAnimationFrame loop so the active cue
    // updates every frame while the video is playing.
    //
    const [activeCue, setActiveCue] = useState(null);
    const activeCueRef = useRef(null);
    const previewSubsOnRef  = useRef(previewSubsOn);
    const previewLangRef    = useRef(previewLang);
    const subsRef           = useRef(subtitlesByLanguage);
    const offsetRef         = useRef(videoStartOffset || 0);
    const cutsRef           = useRef(cuts);
    useEffect(() => { previewSubsOnRef.current  = previewSubsOn; },       [previewSubsOn]);
    useEffect(() => { previewLangRef.current    = previewLang; },         [previewLang]);
    useEffect(() => { subsRef.current           = subtitlesByLanguage; }, [subtitlesByLanguage]);
    useEffect(() => { offsetRef.current         = videoStartOffset || 0; }, [videoStartOffset]);
    useEffect(() => { cutsRef.current           = cuts; },                [cuts]);

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        let rafId;
        const tick = () => {
            rafId = requestAnimationFrame(tick);
            if (!previewSubsOnRef.current) {
                if (activeCueRef.current !== null) {
                    activeCueRef.current = null;
                    setActiveCue(null);
                }
                return;
            }
            const cues = subsRef.current[previewLangRef.current];
            if (!cues) {
                if (activeCueRef.current !== null) {
                    activeCueRef.current = null;
                    setActiveCue(null);
                }
                return;
            }
            const videoTime = Math.max(0, el.currentTime - offsetRef.current);
            // el.currentTime is in the original timeline; the player skips deleted
            // regions by jumping currentTime to cut.endTime.  A cue whose startTime
            // falls inside a deleted segment must be suppressed — its startTime < endTime
            // of a cut means it shouldn't show once we land at cut.endTime.
            const activeCuts = cutsRef.current;
            const found = cues.find((c) => {
                if (videoTime < c.startTime || videoTime >= c.endTime) return false;
                // Suppress cue if its startTime is inside any deleted segment
                const cueStartDeleted = activeCuts.some(
                    (cut) => c.startTime >= cut.startTime && c.startTime < cut.endTime,
                );
                return !cueStartDeleted;
            }) || null;
            // Only call setActiveCue when the cue actually changes (avoids
            // flooding React with re-renders every frame)
            if (found !== activeCueRef.current) {
                activeCueRef.current = found;
                setActiveCue(found);
            }
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    // Intentionally empty deps — all values accessed through refs; effect
    // mounts once when the video element is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Visible logo overlays ──────────────────────────────────── */
    const visibleLogos = (logoTracks || []).filter(
        (lt) => playheadTime >= lt.startTime && playheadTime < lt.endTime,
    );

    // Sync overlay video elements to playhead when present
    useEffect(() => {
        try {
            (visibleLogos || []).filter(l => l.isVideo).forEach((lt) => {
                const el = logoVideoElsRef.current[lt.id];
                if (!el) return;
                const t = playheadTime - lt.startTime;
                if (t >= 0 && t <= (lt.endTime - lt.startTime)) {
                    if (Math.abs(el.currentTime - t) > 0.25) {
                        el.currentTime = Math.max(0, t);
                    }
                    if (isPlaying) {
                        const p = el.play();
                        if (p && p.catch) p.catch(() => {});
                    } else {
                        try { el.pause(); } catch (e) {}
                    }
                } else {
                    try { el.pause(); } catch (e) {}
                }
            });
        } catch (e) {
            // swallow overlay sync errors
        }
    }, [playheadTime, visibleLogos, isPlaying]);

    /* ── Render ─────────────────────────────────────────────────── */

    const cutCount = cuts.length;

    return (
        <MonitorRoot>
            {/* Header bar */}
            <MonitorHeader>
                <Typography variant="caption" sx={{ color: '#888', fontSize: 11, flex: 1 }} noWrap>
                    {videoTitle || 'Program Monitor'}
                </Typography>

                {cutCount > 0 && (
                    <CutBadge>{cutCount} cut{cutCount !== 1 ? 's' : ''} applied</CutBadge>
                )}

                <Tooltip title={muteVideoAudio ? 'Unmute video' : 'Mute video'}>
                    <IconButton
                        size="small"
                        onClick={toggleMuteVideo}
                        sx={{ color: muteVideoAudio ? '#ef9a9a' : '#999', padding: '2px' }}
                    >
                        {muteVideoAudio ? (
                            <VolumeOffIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <VolumeUpIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                </Tooltip>

                {/* Volume slider for preview video */}
                <Box sx={{ display: 'flex', alignItems: 'center', width: 140, gap: 1, ml: 1 }}> 
                    <Slider
                        size="small"
                        min={0}
                        max={1}
                        step={0.05}
                        value={typeof state.videoVolume === 'number' ? state.videoVolume : 1}
                        onChange={(_, v) => setVideoVolume(v)}
                        sx={{ color: '#64b5f6' }}
                    />
                    <Typography variant="caption" sx={{ color: '#888', fontSize: 11, minWidth: 30 }}>
                        {Math.round((state.videoVolume || 1) * 100)}%
                    </Typography>
                </Box>

                <Tooltip title={playOverlayAudio ? 'Disable overlay audio' : 'Enable overlay audio'}>
                    <IconButton
                        size="small"
                        onClick={() => setOverlayAudio(!playOverlayAudio)}
                        sx={{ color: playOverlayAudio ? '#64b5f6' : '#999', padding: '2px' }}
                    >
                        <AudiotrackIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title={previewSubsOn ? 'Hide subtitles' : 'Show subtitles'}>
                    <IconButton
                        size="small"
                        onClick={toggleSubs}
                        sx={{ color: previewSubsOn ? '#64b5f6' : '#999', padding: '2px' }}
                    >
                        {previewSubsOn ? (
                            <ClosedCaptionIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <ClosedCaptionDisabledIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                </Tooltip>
            </MonitorHeader>

            {/* Video */}
            <VideoArea ref={videoAreaRef}>
                {videoUrl ? (
                    <>
                        {/* key forces element remount when URL changes so src updates reliably */}
                        <VideoEl
                            key={videoUrl}
                            ref={videoRef}
                            src={videoUrl}
                            playsInline
                            preload="auto"
                            crossOrigin="anonymous"
                            onLoadedMetadata={(e) => console.log('[VideoEditor] metadata loaded, duration:', e.target.duration, 'src:', e.target.currentSrc)}
                            onError={(e) => console.error('[VideoEditor] Video load error:', e.target.error?.code, e.target.error?.message, videoUrl)}
                        />
                        {/* Overlay container sized/positioned exactly over the video element */}
                        {videoRect.width > 0 && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: videoRect.left,
                                    top: videoRect.top,
                                    width: videoRect.width,
                                    height: videoRect.height,
                                    pointerEvents: 'none',
                                    zIndex: 5,
                                }}
                            >
                                {/* Subtitle overlay */}
                                {previewSubsOn && activeCue && (
                                    <SubOverlay>
                                        <SubText>{activeCue.text}</SubText>
                                    </SubOverlay>
                                )}
                                {/* Logo overlays — positioned within the actual video bounds. Supports images and short overlay videos. */}
                                {visibleLogos.map((lt) => {
                                    const size = `${Math.round((lt.scale ?? 0.2) * 100)}%`;
                                    const commonStyle = {
                                        position: 'absolute',
                                        width: size,
                                        height: 'auto',
                                        maxHeight: size,
                                        objectFit: 'contain',
                                        opacity: lt.opacity ?? 1,
                                        pointerEvents: 'none',
                                        ...(lt.position === 'top-left'     && { top: '4%',  left: '3%' }),
                                        ...(lt.position === 'top-right'    && { top: '4%',  right: '3%' }),
                                        ...(lt.position === 'bottom-left'  && { bottom: '8%', left: '3%' }),
                                        ...(lt.position === 'bottom-right' && { bottom: '8%', right: '3%' }),
                                        ...(lt.position === 'center'       && { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }),
                                    };

                                    if (lt.isVideo) {
                                        return (
                                            <video
                                                key={lt.id}
                                                ref={(el) => { logoVideoElsRef.current[lt.id] = el; }}
                                                src={lt.url}
                                                playsInline
                                                muted={!playOverlayAudio}
                                                preload="metadata"
                                                style={commonStyle}
                                            />
                                        );
                                    }

                                    return (
                                        <img
                                            key={lt.id}
                                            src={lt.url}
                                            alt={lt.labelRole || lt.name}
                                            style={commonStyle}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </>
                ) : (
                    <Typography variant="body2" sx={{ color: '#444' }}>
                        No video loaded
                    </Typography>
                )}
            </VideoArea>

            {/* Transport */}
            <TransportControls videoRef={videoRef} />
        </MonitorRoot>
    );
}
